import {
  faBuilding,
  faEnvelope,
  faQuestionCircle,
  faSave,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import TagSearch from 'components/tags/TagSearch';
import useApi from 'helpers/api';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import ContactsService from 'services/Contacts.service';
import CustomerContactsService from 'services/CustomerContacts.service';

const ContactCustomerEditModal = ({
  data,
  showModal,
  handleClose,
  handleSubmit,
  error,
  refreshData,
  isSuccess
}) => {
  const api = useApi();
  const initialState = {
    id: '',
    name: '',
    email: '',
    phone: '',
    landline: '',
    inactive: false,
    isPortalUser: false,
    isAdmin: false,
    customer: { id: '' },
    tags: []
  };
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [body, setBody] = useState(initialState);
  const [validated, setValidated] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [associatedCustomers, setAssociatedCustomers] = useState([]);
  const [showMenuCustomer, setShowMenuCustomer] = useState(false);
  const [filters, setFilters] = useState({
    size: 100,
    includeInactives: false,
    name: ''
  });
  const [nameMainCustomer, setNameMainCustomer] = useState('');
  const [nameCustomer, setNameCustomer] = useState('');
  const form = useRef(null);

  useEffect(() => {
    if (Object.keys(data).length != 0) {
      setNameMainCustomer(
        (data.customer && data.customer.fantasyName) || data.customer.name
      );
      setBody(data);
      setValidated(false);
      getAssociatedCustomers();
    }
  }, [data]);

  useEffect(() => {
    if (showModal) {
      setBody({
        ...data,
        tags: data.tags.map(tag => tag.name)
      });
      setValidated(false);
      resetCustomerSelection();
    }
  }, [showModal]);

  useEffect(() => {
    getCustomers();
  }, [filters.name]);

  useEffect(() => {
    setFilters(prevFilters => ({ ...prevFilters, name: nameMainCustomer }));
  }, [nameMainCustomer]);

  useEffect(() => {
    setFilters(prevFilters => ({ ...prevFilters, name: nameCustomer }));
  }, [nameCustomer]);

  useEffect(() => {
    if (isSuccess) {
      setBody(initialState);
    }
  }, [isSuccess]);

  const getCustomers = async () => {
    if (filters.name != '') {
      const queryParams = new URLSearchParams(filters).toString();
      let response = await api.customers.list(queryParams);
      setCustomers(response.content);
    }
  };

  const getAssociatedCustomers = async () => {
    let response = await api.customers.listByContact(data.id);
    setAssociatedCustomers(response);
  };

  const associateCustomer = async e => {
    e.preventDefault();
    if (!selectedCustomer) {
      showErrorNotification('Selecione um cliente para associar.');
      return;
    }

    const response = await CustomerContactsService.associate(
      selectedCustomer,
      body.id
    );

    if (response.ok) {
      getAssociatedCustomers();
      refreshData();
      resetCustomerSelection();
    } else error(response);
  };

  const unassociateCustomer = async (e, id) => {
    e.preventDefault();
    let response = await CustomerContactsService.unaassociate(id, body.id);
    if (response.ok) {
      getAssociatedCustomers();
      refreshData();
    } else error(response);
  };

  const onSubmit = e => {
    if (form.current.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      setValidated(false);
      handleSubmit({
        name: body.name,
        email: body.email === '' ? null : body.email,
        phone: body.phone === '' ? null : body.phone,
        landline: body.landline === '' ? null : body.landline,
        inactive: body.inactive,
        isPortalUser: body.isPortalUser,
        isAdmin: !body.isPortalUser ? false : body.isAdmin,
        customer: { id: body.customer.id },
        tags: body.tags
      });
    }
  };

  const handleCustomerSelect = customer => {
    setNameCustomer(customer.fantasyName || customer.name);
    setSelectedCustomer(customer.id);
    setShowMenuCustomer(false);
  };

  function resetCustomerSelection() {
    setNameCustomer('');
    setSelectedCustomer(null);
    setShowMenuCustomer(false);
  }

  const resendInvite = async () => {
    const response = await ContactsService.resendInvite(data.id);
    if (response.ok) {
      showSuccessNotification('Convite reenviado com sucesso.');
    } else {
      showErrorNotification(`Ocorreu um erro ao reenviar o convite.`);
    }
  };

  const handleAddTag = tag => {
    if (tag.trim() !== '' && !body.tags.includes(tag.trim())) {
      setBody(prevState => ({
        ...prevState,
        tags: [...prevState.tags, tag.trim()]
      }));
    }
  };

  const handleRemoveTag = tag => {
    setBody(prevState => ({
      ...prevState,
      tags: prevState.tags.filter(t => t !== tag)
    }));
  };

  return (
    <Modal show={showModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Editar contato</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>
              Nome <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Nome"
              autoFocus
              value={body.name}
              onChange={e =>
                setBody(prevState => ({ ...prevState, name: e.target.value }))
              }
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um nome.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>
              E-mail{' '}
              {(body.isPortalUser ||
                (!body.phone?.trim() && !body.landline?.trim())) && (
                <span className="text-danger">*</span>
              )}
            </Form.Label>
            <Form.Control
              required={
                (!body.phone?.trim() && !body.landline?.trim()) ||
                body.isPortalUser
              }
              type="email"
              placeholder="E-mail"
              value={body.email}
              onChange={e =>
                setBody(prevState => ({ ...prevState, email: e.target.value }))
              }
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um e-mail.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="agent.phone">
            <Form.Label>
              Telefone{' '}
              {!body.email && !body.phone && (
                <span className="text-danger">*</span>
              )}
            </Form.Label>
            <MaskedInput
              mask={[
                '(',
                /[1-9]/,
                /\d/,
                ')',
                ' ',
                /\d/,
                /\d/,
                /\d/,
                /\d/,
                '-',
                /\d/,
                /\d/,
                /\d/,
                /\d/
              ]}
              placeholder="(00) 0000-0000"
              required={!body.email && !body.phone}
              guide={false}
              value={body.landline}
              onChange={e =>
                setBody(prevBody => ({ ...prevBody, landline: e.target.value }))
              }
              render={(ref, props) => <Form.Control ref={ref} {...props} />}
            />

            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um número de telefone.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="agent.phone">
            <Form.Label>
              Celular{' '}
              {!body.email && !body.landline && (
                <span className="text-danger">*</span>
              )}
            </Form.Label>
            <MaskedInput
              mask={[
                '(',
                /[1-9]/,
                /\d/,
                ')',
                ' ',
                /\d/,
                /\d/,
                /\d/,
                /\d/,
                /\d/,
                '-',
                /\d/,
                /\d/,
                /\d/,
                /\d/
              ]}
              placeholder="(00) 00000-0000"
              required={!body.email && !body.landline}
              guide={false}
              value={body.phone}
              onChange={e =>
                setBody(prevBody => ({ ...prevBody, phone: e.target.value }))
              }
              render={(ref, props) => <Form.Control ref={ref} {...props} />}
            />

            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um número de celular.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tags</Form.Label>
            <TagSearch onAddTag={handleAddTag} />
            <div className="mt-1">
              {body.tags.map((tag, index) => (
                <Badge
                  key={index}
                  bg="light"
                  text="dark"
                  className="me-1 p-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleRemoveTag(tag)}
                >
                  {tag} &times;
                </Badge>
              ))}
            </div>
          </Form.Group>

          <Form.Group as={Flex} className="mb-2">
            <Flex
              alignItems="center"
              justifyContent="between"
              className="me-3 w-100"
            >
              <Form.Label>Ativo</Form.Label>
              <Form.Check
                type="switch"
                value={body.inactive}
                checked={!body.inactive}
                onChange={e => setBody({ ...body, inactive: !body.inactive })}
              />
            </Flex>
          </Form.Group>

          <Form.Group as={Flex} className="mb-1">
            <Flex
              alignItems="center"
              justifyContent="between"
              className="me-3 w-100"
            >
              <Form.Label>Tem acesso ao portal do cliente?</Form.Label>
              <Form.Check
                type="switch"
                value={body.isPortalUser}
                checked={body.isPortalUser}
                onChange={e =>
                  setBody({ ...body, isPortalUser: !body.isPortalUser })
                }
              />
            </Flex>
          </Form.Group>

          <Form.Group as={Flex} className="ms-3 mb-3">
            {body.isPortalUser && (
              <Flex
                alignItems="center"
                justifyContent="between"
                className="me-3 w-100"
              >
                <Form.Label>
                  Administrador
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        Usuários administradores podem visualizar todos os
                        tickets dos clientes associados a eles.
                      </Tooltip>
                    }
                  >
                    <span className="ms-1">
                      <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                    </span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Check
                  type="switch"
                  value={body.isAdmin}
                  checked={body.isAdmin}
                  onChange={e => setBody({ ...body, isAdmin: !body.isAdmin })}
                />
              </Flex>
            )}
          </Form.Group>

          <Form.Label>Associe um cliente</Form.Label>
          <Form.Group className="w-100">
            <Row className="d-flex align-items-center">
              <Col md={9}>
                <SearchInputWithSuggestions
                  searchTerm={nameCustomer}
                  placeholder="Digite para pesquisar..."
                  suggestions={customers}
                  showSuggestions={showMenuCustomer}
                  onSearchChange={e => {
                    setNameCustomer(e.target.value);
                    setShowMenuCustomer(e.target.value.length > 0);
                  }}
                  onSuggestionSelect={handleCustomerSelect}
                  renderSuggestionItem={customer => (
                    <div
                      key={customer.id}
                      className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                      onClick={() => handleCustomerSelect(customer)}
                      role="button"
                      style={{
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <div className="fw-bold">{customer.name}</div>
                      <div className="text-muted small">
                        {customer.type === 'JURIDICAL_PERSON' ? (
                          <>
                            <FontAwesomeIcon
                              icon={faBuilding}
                              className="me-1"
                            />{' '}
                            Pessoa Jurídica
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faUser} className="me-1" />{' '}
                            Pessoa Física
                          </>
                        )}
                      </div>
                    </div>
                  )}
                />
              </Col>
              <Col md={3} className="p-0">
                <Button variant="primary" size="sm" onClick={associateCustomer}>
                  + Adicionar
                </Button>
              </Col>
            </Row>
          </Form.Group>
        </Form>
        <div className="table-responsive scrollbar">
          <table className="table fs-10 mb-0 mt-3">
            <thead className="bg-200 text-900">
              <tr>
                <th>Clientes associados</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {associatedCustomers.map((item, i) => (
                <tr key={i}>
                  <td data-value={item.id}>
                    <span className="d-flex">
                      <Form.Check
                        type="radio"
                        value={body.customer.id}
                        checked={body.customer.id == item.id}
                        className="me-1"
                        onChange={() =>
                          setBody(prevFilters => ({
                            ...prevFilters,
                            customer: { id: item.id }
                          }))
                        }
                      />
                      {item.fantasyName || item.name}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-link text-danger btn-sm"
                      onClick={e => unassociateCustomer(e, item.id)}
                    >
                      x
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          Fechar
        </Button>
        {data && data.isPortalUser && (
          <OverlayTrigger
            overlay={
              <Tooltip>
                Reenviar convite para acesso ao portal do cliente
              </Tooltip>
            }
          >
            <Button variant="falcon-default" onClick={resendInvite}>
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Reenviar convite
            </Button>
          </OverlayTrigger>
        )}
        <Button variant="primary" onClick={onSubmit}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ContactCustomerEditModal;
